if (!customElements.get('sticky-buy-bar')) {
  class StickyBuyBar extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.sectionId;
      this.atcBtn = this.querySelector('[data-action="add-to-cart"]');
      this.buyNowBtn = this.querySelector('[data-action="buy-now"]');
      this.currentPriceEl = this.querySelector('.sticky-buy-bar__price--current');
      this.comparePriceEl = this.querySelector('.sticky-buy-bar__price--compare');
      this.discountBadgeEl = this.querySelector('.sticky-buy-bar__discount-badge');
      this.priceContainer = this.querySelector('.sticky-buy-bar__price');
      this.isPreorder = this.dataset.isPreorder === 'true';
      this.isOutOfStock = this.dataset.isOutOfStock === 'true';
      this.isPartnerStore = this.dataset.partnerStore === 'true';
      this._cartDrawerOpen = false;
    }

    connectedCallback() {
      this._onVariantChange = this.handleVariantChange.bind(this);
      this._onAvailabilityChange = this.handleAvailabilityChange.bind(this);
      if (!this.isPartnerStore) {
        document.addEventListener('variant:change', this._onVariantChange);
        document.addEventListener('variant:availability-change', this._onAvailabilityChange);
      }

      if (this.atcBtn) {
        this.atcBtn.addEventListener('click', () => this.handleAddToCart());
      }
      if (this.buyNowBtn) {
        this.buyNowBtn.addEventListener('click', () => this.handleBuyNow());
      }

      this.observeCartDrawer();
      this.observeMainATC();
      this.observeVisualViewport();
    }

    disconnectedCallback() {
      document.removeEventListener('variant:change', this._onVariantChange);
      document.removeEventListener('variant:availability-change', this._onAvailabilityChange);
      if (this._atcObserver) this._atcObserver.disconnect();
      const vv = window.visualViewport;
      if (vv && this._onViewportChange) {
        vv.removeEventListener('resize', this._onViewportChange);
        vv.removeEventListener('scroll', this._onViewportChange);
      }
    }

    /* ---- Scroll-based visibility (desktop only) ---- */

    observeMainATC() {
      // Mobile: always visible (main ATC is below the fold on mobile)
      if (window.innerWidth < 750) return;

      const mainATC = this.isPartnerStore
        ? document.querySelector(`#MainProduct-${this.sectionId} [data-partner-store-link]`)
        : document.querySelector(
        `#ProductSubmitButton-${this.sectionId}`
      ) || document.querySelector('.product-form__submit[name="add"]');
      if (!mainATC) return;

      // Desktop: start hidden, show when main ATC scrolls out
      this.classList.add('is-hidden');

      this._atcObserver = new IntersectionObserver(
        ([entry]) => {
          if (!this._cartDrawerOpen) {
            this.classList.toggle('is-hidden', entry.isIntersecting);
          }
        },
        { threshold: 0 }
      );
      this._atcObserver.observe(mainATC);
    }

    /* ---- Visual Viewport compensation (mobile only) ---- */

    observeVisualViewport() {
      if (window.innerWidth >= 750) return;
      const vv = window.visualViewport;
      if (!vv) return;

      this._onViewportChange = () => {
        if (this.classList.contains('is-hidden')) return;
        const offset = window.innerHeight - (vv.height + vv.offsetTop);
        if (offset > 1) {
          this.style.transform = `translateY(${offset}px)`;
        } else {
          this.style.transform = '';
        }
      };

      vv.addEventListener('resize', this._onViewportChange);
      vv.addEventListener('scroll', this._onViewportChange);
    }

    /* ---- Variant sync ---- */

    handleVariantChange(event) {
      const { variant } = event.detail;
      if (!variant) {
        this.setUnavailable();
        return;
      }
      this.isOutOfStock = this.getVariantOutOfStock(variant);
      this.updatePrice(variant);
      this.updateButtons(variant);
    }

    handleAvailabilityChange(event) {
      const { variant, isOutOfStock } = event.detail || {};
      if (!variant || String(variant.id) !== String(this.getVariantId())) return;
      this.isOutOfStock = Boolean(isOutOfStock);
      this.updateButtons(variant);
    }

    getVariantOutOfStock(variant) {
      if (variant.inventory_management === 'shopify' && variant.inventory_quantity != null) {
        return Number(variant.inventory_quantity) <= 0;
      }
      return variant.available === false;
    }

    getVariantId() {
      const form = document.getElementById(`product-form-${this.sectionId}`);
      if (!form) return null;
      const input = form.querySelector('input[name="id"]');
      return input ? input.value : null;
    }

    getQuantity() {
      const input = document.querySelector(`#Quantity-${this.sectionId}`);
      return input ? parseInt(input.value, 10) || 1 : 1;
    }

    updatePrice(variant) {
      if (this.currentPriceEl && variant.price != null) {
        this.currentPriceEl.textContent = this.formatMoney(variant.price);
      }

      const hasDiscount = variant.compare_at_price && variant.compare_at_price > variant.price;

      if (this.comparePriceEl) {
        if (hasDiscount) {
          this.comparePriceEl.textContent = this.formatMoney(variant.compare_at_price);
          this.comparePriceEl.style.display = '';
        } else {
          this.comparePriceEl.style.display = 'none';
        }
      }

      if (hasDiscount) {
        const pct = Math.round((variant.compare_at_price - variant.price) / variant.compare_at_price * 100);
        if (!this.discountBadgeEl && this.priceContainer) {
          const badge = document.createElement('span');
          badge.className = 'sticky-buy-bar__discount-badge';
          this.priceContainer.insertBefore(badge, this.currentPriceEl);
          this.discountBadgeEl = badge;
        }
        if (this.discountBadgeEl) {
          this.discountBadgeEl.textContent = `-${pct}%`;
          this.discountBadgeEl.style.display = '';
        }
      } else if (this.discountBadgeEl) {
        this.discountBadgeEl.style.display = 'none';
      }
    }

    updateButtons(variant) {
      // A product can be tagged for pre-order while an individual variant is in stock.
      // Match the main product form: only an out-of-stock variant uses the pre-order CTA.
      const isPreOrder = this.isPreorder && this.isOutOfStock;

      if (variant.available || isPreOrder) {
        if (this.atcBtn) {
          this.atcBtn.disabled = false;
          const span = this.atcBtn.querySelector('span');
          if (span) {
            span.textContent = window.variantStrings?.addToCart || 'Add to Cart';
          }
        }
        if (this.buyNowBtn && !this.isOutOfStock) {
          this.buyNowBtn.disabled = false;
          this.buyNowBtn.style.display = '';
          if (isPreOrder) {
            this.buyNowBtn.classList.add('sticky-buy-bar__btn--preorder');
            const span = this.buyNowBtn.querySelector('span');
            if (span) span.textContent = 'PREORDER';
          } else {
            this.buyNowBtn.classList.remove('sticky-buy-bar__btn--preorder');
            const span = this.buyNowBtn.querySelector('span');
            if (span) span.textContent = window.variantStrings?.buyNow || 'Buy Now';
          }
        } else if (this.buyNowBtn) {
          this.buyNowBtn.disabled = !isPreOrder;
          this.buyNowBtn.style.display = isPreOrder ? '' : 'none';
          const span = this.buyNowBtn.querySelector('span');
          if (span) span.textContent = isPreOrder ? 'PREORDER' : window.variantStrings?.soldOut || 'Sold Out';
          this.buyNowBtn.classList.toggle('sticky-buy-bar__btn--preorder', isPreOrder);
        }
      } else {
        if (this.atcBtn) {
          this.atcBtn.disabled = true;
          const span = this.atcBtn.querySelector('span');
          if (span) span.textContent = window.variantStrings?.soldOut || 'Sold Out';
        }
        if (this.buyNowBtn) {
          this.buyNowBtn.disabled = true;
          this.buyNowBtn.style.display = 'none';
        }
      }
    }

    setUnavailable() {
      if (this.atcBtn) {
        this.atcBtn.disabled = true;
        const span = this.atcBtn.querySelector('span');
        if (span) span.textContent = window.variantStrings?.unavailable || 'Unavailable';
      }
      if (this.buyNowBtn) {
        this.buyNowBtn.disabled = true;
        this.buyNowBtn.style.display = 'none';
      }
    }

    formatMoney(cents) {
      const amount = cents / 100;
      return new Intl.NumberFormat(Shopify.locale || 'en', {
        style: 'currency',
        currency: Shopify.currency?.active || 'EUR',
      }).format(amount);
    }

    /* ---- Button loading state ---- */

    setLoading(btn, loading) {
      if (!btn) return;
      const spinner = btn.querySelector('.loading-overlay__spinner');
      if (loading) {
        btn.disabled = true;
        btn.classList.add('loading');
        if (spinner) spinner.classList.remove('hidden');
      } else {
        btn.disabled = false;
        btn.classList.remove('loading');
        if (spinner) spinner.classList.add('hidden');
      }
    }

    /* ---- Error feedback ---- */

    showButtonError(btn, message) {
      if (!btn) return;
      const span = btn.querySelector('span');
      if (!span) return;
      const originalText = span.textContent;
      span.textContent = message || 'Error';
      btn.style.backgroundColor = '#dc3545';
      btn.style.color = '#fff';
      setTimeout(() => {
        span.textContent = originalText;
        btn.style.backgroundColor = '';
        btn.style.color = '';
      }, 2500);
    }

    /* ---- GA4 tracking helper ---- */

    fireGA4AddToCart(variantId, qty) {
      if (typeof gtag !== 'function') return;
      const ga4DataEl = document.getElementById('ga4-product-data-' + this.sectionId);
      if (!ga4DataEl) return;
      try {
        const ga4Data = JSON.parse(ga4DataEl.textContent);
        const variantInfo = ga4Data.variants[variantId];
        if (variantInfo) {
          gtag('event', 'add_to_cart', {
            currency: ga4Data.currency,
            value: variantInfo.price * qty,
            items: [{
              item_id: variantInfo.item_id,
              item_name: variantInfo.item_name,
              item_brand: variantInfo.item_brand,
              item_category: variantInfo.item_category,
              item_variant: variantInfo.item_variant,
              price: variantInfo.price,
              quantity: qty
            }]
          });
        }
      } catch (e) { /* GA4 tracking error, fail silently */ }
    }

    /* ---- Add to Cart ---- */

    async handleAddToCart() {
      const variantId = this.getVariantId();
      if (!variantId) return;

      const qty = this.getQuantity();
      this.fireGA4AddToCart(variantId, qty);
      this.setLoading(this.atcBtn, true);

      try {
        const cartDrawer = document.querySelector('cart-drawer');
        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', this.getQuantity());

        if (cartDrawer) {
          formData.append(
            'sections',
            cartDrawer.getSectionsToRender().map((s) => s.id)
          );
          formData.append('sections_url', window.location.pathname);
          cartDrawer.setActiveElement(document.activeElement);
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;

        const response = await fetch(routes.cart_add_url, config);
        const data = await response.json();

        if (data.status) {
          this.showButtonError(this.atcBtn, data.description);
          return;
        }

        document.dispatchEvent(
          new CustomEvent('variant:add', {
            detail: {
              variant: { id: variantId },
              quantity: this.getQuantity(),
            },
          })
        );

        document.dispatchEvent(
          new CustomEvent('cart:change', {
            detail: data,
            bubbles: true,
          })
        );

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'sticky-buy-bar',
            productVariantId: variantId,
          });
        }

        if (cartDrawer) {
          cartDrawer.renderContents(data);
        } else {
          window.location = window.routes.cart_url;
        }
      } catch (err) {
        console.error('Sticky buy bar: add to cart failed', err);
      } finally {
        this.setLoading(this.atcBtn, false);
      }
    }

    /* ---- Buy Now ---- */

    async handleBuyNow() {
      const variantId = this.getVariantId();
      if (!variantId) return;

      this.fireGA4AddToCart(variantId, this.getQuantity());
      this.setLoading(this.buyNowBtn, true);

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify({
            items: [
              {
                id: parseInt(variantId, 10),
                quantity: this.getQuantity(),
              },
            ],
          }),
        });

        if (!response.ok) {
          const data = await response.json();
          this.showButtonError(this.buyNowBtn, data.description);
          return;
        }

        window.location.href = '/checkout';
      } catch (err) {
        window.location.href = '/cart';
      }
    }

    /* ---- Cart drawer interaction ---- */

    observeCartDrawer() {
      const cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer) return;

      const observer = new MutationObserver(() => {
        this._cartDrawerOpen =
          cartDrawer.hasAttribute('open') ||
          cartDrawer.classList.contains('active') ||
          cartDrawer.classList.contains('is-open');

        if (this._cartDrawerOpen) {
          this.style.transform = '';
          this.classList.add('is-hidden');
        } else if (!this._atcObserver) {
          // No scroll observer (mobile) — always show when drawer closes
          this.classList.remove('is-hidden');
        }
        // If _atcObserver exists (desktop), let the observer handle show/hide
      });

      observer.observe(cartDrawer, {
        attributes: true,
        attributeFilter: ['open', 'class'],
      });
    }
  }

  customElements.define('sticky-buy-bar', StickyBuyBar);
}
